import { Component, OnInit, Input, Output, EventEmitter, ViewEncapsulation, OnDestroy, ElementRef, Optional, Self, DoCheck, ViewChild } from '@angular/core';
import { FormControl, ControlValueAccessor, NgControl} from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

@Component({
	selector: 'tr2-inputtext',
	templateUrl: './inputtext.component.html',
	styleUrls: ['./inputtext.component.scss'],
	providers: [
		{ provide: MatFormFieldControl, useExisting: Inputtext }
	],
	host: {
		'[class.inputtext-floating]': 'shouldLabelFloat',
		'[id]': 'id',
		'[attr.aria-describedby]': 'describedBy'
	},
	encapsulation: ViewEncapsulation.None
})
export class Inputtext implements MatFormFieldControl<string>, OnDestroy, ControlValueAccessor, OnInit, DoCheck {

	/****** Definimos variables de clase ******/
	static nextId = 0;
	controlType = 'tr2-inputtext';
	id = `tr2-inputtext-${Inputtext.nextId++}`;
	control = new FormControl(null);
	focused = false;
	errorState = false;
	stateChanges = new Subject<void>();
	describedBy = '';
	readonly = false;
	invalid = false;
	isSimpleTextField = false;
	tipoCampo = 'text';
	min = null;
	max = null;
	step = null;
	rows = 3;
	pattern = false;
	
	/** Variable que asigna el atributo name al input */
	@Input()
	get name(): any[] {
		return this._name;
	}
	set name(value: any[]) {
		this._name = value;
		this.stateChanges.next();
	}
	private _name: any[];

	/** Variable que setea el placeholder de la entrada */
	@Input()
	get placeholder(): string {
		return this._placeholder;
	}
	set placeholder(value: string) {
		this._placeholder = value;
		this.stateChanges.next();
	}
	private _placeholder: string;

	/** Variable que indica si el campo es requerido para el formulario */
	@Input()
	get required(): boolean {
		if (this._required) {
		return this._required;
		}

		// cuando el control retorna un error desde la validación con un valor vacío
		if (this.ngControl && this.ngControl.control && this.ngControl.control.validator) {
			const emptyValueControl = Object.assign({}, this.ngControl.control);
			(emptyValueControl as any).value = null;
			return 'required' in (this.ngControl.control.validator(emptyValueControl) || {});
		}
		return false;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
		this.stateChanges.next();
	}
	private _required = false;

	/** Variable para setear si el campo de entrada está deshabilitado o no */
	@Input()
	get disabled(): boolean {
		if (this.ngControl && this.ngControl.control && this.ngControl.control.disabled) {
			this._disabled = this.ngControl.control.disabled;
		}
		return this._disabled;
	}
	set disabled(value: boolean) {
		this._disabled = coerceBooleanProperty(value);
		this._disabled ? this.control.disable() : this.control.enable();
		this.stateChanges.next();
	}
	private _disabled = false;

	/** Variable en donde se captura el valor de la entrada */
	@Input()
	get value(): any {
		return this.control.value;
	}
	set value(val: any) {
		this.control.setValue(val);
		this.stateChanges.next();
	}

	/** Función que indica si la etiqueta de la entrada pasa a flotante o no */
	get shouldLabelFloat() {
		return this.focused || !this.empty;
	}

	/** Función que indica si la entrada del formulario esta vacía */
	get empty() {
		return !this.control || this.control.value === null || this.control.value === '';
	}

	/** Si utiliza atributo autofocus */
	@Input() autoFocus = false;

	/** tabindex, no utiliza directiva FocusNext */
	@Input() tab = -1;

	//////////////////////////////////
	// Bindings personalizados
	//////////////////////////////////
	@Input() formControlName: string;
	@Output() change: EventEmitter<string> = new EventEmitter<string>();
	@Output() keyup: EventEmitter<string> = new EventEmitter<string>();
	@ViewChild('inputtext', { static: false }) inputElement: ElementRef;

	constructor(
		private fm: FocusMonitor,
		private elRef: ElementRef<HTMLElement>,
		@Optional() @Self() public ngControl: NgControl,
	) {
		if (this.ngControl != null) {
			this.ngControl.valueAccessor = this;
		}

		fm.monitor(elRef, true).subscribe(origin => {
			this.focused = !!origin;
			this.stateChanges.next();
		});
	}

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	writeValue() { }

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	registerOnChange() { }

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	registerOnTouched() { }

	ngOnDestroy() {
		this.stateChanges.complete();
		this.fm.stopMonitoring(this.elRef);
	}

	setDescribedByIds(ids: string[]) {
		this.describedBy = ids.join(' ');
	}

	/// tal vez deberia ser mas especifico que solo input?
	onContainerClick(event: MouseEvent) {
		const element = (event.target as Element).tagName.toLowerCase();
		if (element != 'input' && element != 'textarea') {
			if (this.elRef.nativeElement.querySelector('input')) {
				this.elRef.nativeElement.querySelector('input').focus();
			}
			if (this.elRef.nativeElement.querySelector('textarea')) {
				this.elRef.nativeElement.querySelector('textarea').focus();
			}
		}
	}

	/////////////////////////////////////////////////////////////////////////////////////////////////
	/////////////////////////////////////////////////////////////////////////////////////////////////
	ngOnInit() {
		// setemos y enlazamos el formControl al del input del inputtext
		this.control = this.ngControl.control as FormControl;

		if (this.control['tipo']) {
			this.tipoCampo = this.control['tipo'];
		}
		if (this.control['attrs']) {
			this.placeholder = this.control['attrs']['placeholder'] ? this.control['attrs']['placeholder'] : '';
			this.disabled = this.control['attrs']['disabled'] ? this.control['attrs']['disabled'] : false;
			this.readonly = this.control['attrs']['readonly'] ? this.control['attrs']['readonly'] : false;
			this.min = this.control['attrs']['min'] ? this.control['attrs']['min'] : null;
			this.max = this.control['attrs']['max'] ? this.control['attrs']['max'] : null;
			this.step = this.control['attrs']['step'] ? this.control['attrs']['step'] : null;
			this.rows = this.control['attrs']['rows'] ? this.control['attrs']['rows'] : 3;
			this.pattern = this.control['attrs']['pattern'] ? this.control['attrs']['pattern'] : '';

		}
	}

	onChange(inputtext: string) {
		this.change.emit(inputtext);
	}

	onBlur(event) {
		// this.change.emit(event);
		this.setTouched();
	}

	focus() {
		this.inputElement.nativeElement.focus();
	}

	onFocus(event) {
		if (event.target.select) {
			event.target.select();
		}
	}

	setTouched(): void {
		if (!this.control.touched) {
			this.control.markAsTouched();
			this.errorState = this.control.invalid;
		}
		this.stateChanges.next();
	}

	ngDoCheck() {
		this.errorState = this.ngControl.invalid && (this.ngControl.touched || this.ngControl.dirty);
	}
}
